import React, { useState, useEffect, useRef } from 'react';
import { LVP_DEPTS, lvpResolveFnVolKey, lvpVols } from './LVP';
import { QUARTERS, QIDS, PLAN_KINDS, loadPlanIndex, loadPlanVersion, savePlanVersion } from '../msmPlans';
import { getUser } from '../auth';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];


// Primary demand paths (drive the level-load scheduler)
const PATHS = [
  { key: 'pid', label: 'PID / Sorter', defRate: 300, crewStep: 2, min: 2, max: 8 },
  { key: 'pr', label: 'PR', defRate: 220, crewStep: 2, min: 2, max: 8 },
  { key: 'aros', label: 'AROS', defRate: 550, crewStep: 2, min: 2, max: 2 },
  { key: 'pit', label: 'PIT / SKID', defRate: 1065, crewStep: 1, min: 0, max: 2 },
];

// Allocation rule per role, keyed by WEESA casual name.
// types: primary | arosOn | arosMin | fixed | flexPath | ratioStep | ratioMul | condGt | demandFluid | manual
const RULE = {
  'PID Unload CPLH': { type: 'primary', path: 'pid' },
  'PID Divert CPLH': { type: 'ratioStep', anchor: 'pid', steps: [[6, 1], [8, 2]], min: 0, max: 2 },
  'AROS Unload': { type: 'primary', path: 'aros' },
  // 1 or 2 HC (LVP hours pick within the range) while AROS runs
  'AROS CSX Label': { type: 'arosRange', min: 1, max: 2 },
  'AROS Avery': { type: 'arosRange', min: 1, max: 2 },
  'AROS Divert': { type: 'arosOn', count: 1 },
  'AROS Sort': { type: 'arosMin', count: 3, min: 3, max: 6 },
  'AROS Waterspider': { type: 'arosMin', count: 2, min: 2, max: 4 },
  'AROS Operator': { type: 'arosLvp' },
  'Re-inject': { type: 'arosLvp' },
  'PR Unload CPLH': { type: 'primary', path: 'pr' },
  'Pallet Build CPLH': { type: 'ratioMul', anchor: 'pr', mul: 1, min: 2, max: 12 },
  'Pallet Wrap/Lantech CPLH': { type: 'prOn', count: 1, max: 3 }, // >=1 if PR running
  'PR Waterspider': { type: 'heavyOff', min: 1, max: 4 },
  'IB Pallet EPJ Waterspider': { type: 'heavyOn', min: 1, max: 3 },
  'EPJ Spotter': { type: 'mirror', ofCasual: 'IB Pallet EPJ Waterspider' }, // 1:1 with EPJ Waterspider
  'PIT Unloader': { type: 'lvp' }, // seed from LVP planned hours (not the low SKID goal)
  'EOL Avery': { type: 'range', min: 1, max: 2 }, // 1 or 2 HC from LVP hours
  'EOL Sort': { type: 'flexPath', path: 'pid', min: 2, max: 5 },
  'EOL Waterspider': { type: 'flexPath', path: 'pid', min: 1, max: 3 },
  'EOL Divert': { type: 'condGt', anchorCasual: 'EOL Sort', thresh: 4, count: 1, min: 0, max: 1 },
  'Fluid Load': { type: 'demandFluid' },
  'Wall Builder': { type: 'ratioMul', anchor: 'fluid', mul: 2, min: 2, max: 14 }, // Offline Wallbuild
  'Sorter Wallbuilder': { type: 'flexPath', path: 'pid', min: 5, max: 20, slack: true },
  'Jam Clear': { type: 'flexPath', path: 'pid', min: 2, max: 3 },
  'Fluid WS': { type: 'condGt', anchorCasual: 'Wall Builder', thresh: 6, count: 3, min: 0, max: 3 },
  'Pallet Loader CPLH': { type: 'flexPath', path: 'pit', min: 0, max: 5 },
  'OB EPJ Operator CPLH': { type: 'flexPath', path: 'pit', min: 0, max: 5 },
  'IB TDR': { type: 'fixed', count: 1, min: 1, max: 3 },
  'IB Clerk': { type: 'fixed', count: 1 },
  'OB TDR': { type: 'fixed', count: 2, min: 2, max: 3 },
  'IB PSolver': { type: 'psolve', min: 2, max: 6 }, // min 4 when High Piles
  'Ship Clerk': { type: 'fixed', count: 1 },
};

// Departments mirrored from the WEESA tab (line-item rollup rows omitted)
const DEPTS = [
  { key: 'pid', name: 'PID', functions: [
    { casual: 'PID Unload CPLH', fn: 'Vendor Line Load' }, { casual: 'PID Divert CPLH', fn: 'PID Manual Divert' },
    { casual: 'PID Inject', fn: 'Receive Line Loader' }, { casual: 'PID Operator', fn: 'Receive Dock Crew' } ] },
  { key: 'aros', name: 'AROS', functions: [
    { casual: 'AROS Unload', fn: 'ReceiveLine Unloader' }, { casual: 'AROS Operator', fn: 'Receive Dock Crew' },
    { casual: 'AROS CSX Label', fn: 'PID Sort Line' }, { casual: 'AROS Avery', fn: 'MHE Support' },
    { casual: 'AROS Divert', fn: 'Robotic Problem Solv' }, { casual: 'AROS Sort', fn: 'IDRT' },
    { casual: 'AROS Waterspider', fn: 'Robotic Water Spider' }, { casual: 'Re-inject', fn: 'Trans Out Overflow' } ] },
  { key: 'ibPR', name: 'IB PR', functions: [
    { casual: 'PR Unload CPLH', fn: 'Vendor Sort' }, { casual: 'Pallet Build CPLH', fn: 'Offline_Mods' },
    { casual: 'Pallet Receive', fn: 'Pallet Receive' },
    { casual: 'Pallet Wrap/Lantech CPLH', fn: 'IB Robotics Operator' }, { casual: 'PR Waterspider', fn: 'Rcv WaterSpider' },
    { casual: 'IB Pallet EPJ Waterspider', fn: 'Case Rcv WaterSpider' }, { casual: 'Pallet Auditors', fn: 'General Audits' },
    { casual: 'EPJ Spotter', fn: 'PID Gatekeep' } ] },
  { key: 'ibPIT', name: 'IB PIT', functions: [
    { casual: 'PIT Pallet Build', fn: 'ReceiveParcel Unload' }, { casual: 'IB PIT Waterspider', fn: 'Vendor Cutter' },
    { casual: 'PIT Unloader', fn: 'PID Truck Unload' } ] },
  { key: 'sorterEOL', name: 'Sorter EOL', functions: [
    { casual: 'EOL Avery', fn: 'Tote Replenishment' }, { casual: 'EOL Divert', fn: 'Transfer Out Kaizen' },
    { casual: 'EOL Sort', fn: 'IDRT' }, { casual: 'EOL Waterspider', fn: 'Water Spider' } ] },
  { key: 'fluid', name: 'Fluid', functions: [
    { casual: 'Fluid WS', fn: 'Transfer Out Packing' }, { casual: 'Fluid Load', fn: 'Fluid Load - Case' },
    { casual: 'Sorter Wallbuilder', fn: 'Wall Builder' }, { casual: 'Jam Clear', fn: 'MHE Support' },
    { casual: 'Wall Builder', fn: 'Robotics Operator' } ] },
  { key: 'obPR', name: 'OBPR', functions: [
    { casual: 'OB EPJ WaterSpider', fn: 'Outbound Dock Crew' }, { casual: 'Pallet Loader CPLH', fn: 'Dock Pallet Loader' },
    { casual: 'OB PIT OPERATOR', fn: 'Truck Loader' }, { casual: 'OB EPJ Operator CPLH', fn: 'IXD Pallet Loader' } ] },
  { key: 'ibSupport', name: 'IB Support', functions: [
    { casual: 'IB PSolver', fn: 'Receive ProblemSolve' }, { casual: 'IB Lead/PA', fn: 'Receive Lead/PA' },
    { casual: 'LP Receive', fn: 'LP Receive' }, { casual: 'Each Receive', fn: 'Each Receive' }, { casual: 'Prep Receive', fn: 'Prep Receive' },
    { casual: 'IB TDR', fn: 'Receive Dock TDR' },
    { casual: 'IB Training', fn: 'Receive Training' }, { casual: 'IB Ambassador', fn: 'Receive Ambassador' },
    { casual: 'IB Clerk', fn: 'Inbound Dock Clerk' } ] },
  { key: 'obSupport', name: 'OB Support', functions: [
    { casual: 'OB PS', fn: 'TransferOut PSolve' }, { casual: 'DA Lead/PA', fn: 'Transfer Out Lead/PA' }, { casual: 'OB Training', fn: 'TransferOut Training' },
    { casual: 'OB Ambassador', fn: 'Transfer Out Ambssdr' }, { casual: 'Ship Clerk', fn: 'Ship Clerk' },
    { casual: 'OB TDR', fn: 'TransferOut DockCrew' } ] },
  // Standalone: rendered as one row below OB Support Total (no header / total row)
  { key: 'admin', name: 'Admin', standalone: true, functions: [ { casual: 'Admin', fn: 'Admin/HR/IT (LVP)' } ] },
];

// MSM role -> LVP role name where they differ (otherwise the " CPLH" suffix is stripped).
const LVP_ALIAS = {
  'EOL Sort': 'IDRT EOL', 'AROS Sort': 'IDRT AROS', 'IB Pallet EPJ Waterspider': 'IB Pallet EPJ WS',
  'OB PIT OPERATOR': 'OB PIT Operator', 'IB PSolver': 'IB Problem Solve', 'OB PS': 'DA Problem Solve',
  'Re-inject': 'Trans Out Overflow',
};
const lvpCasualOf = (casual) => LVP_ALIAS[casual] || casual.replace(/ CPLH$/, '');
const LVP_ROLE = {}; // LVP casual -> { dept, fn }
LVP_DEPTS.forEach(dept => dept.functions.forEach(f => { LVP_ROLE[f.casual] = { dept, fn: f.fn }; }));
const LVP_DEPT_NAMES = LVP_DEPTS.map(dp => dp.name);
// Roles LVP counts as Inbound outside the IB depts (LVP getIBHours): AROS IB functions + both IDRT lines.
const IB_EXTRA_ROLES = new Set(['AROS Unload', 'AROS Operator', 'AROS CSX Label', 'AROS Sort', 'EOL Sort']);
const IB_DEPT_KEYS = ['pid', 'ibPR', 'ibPIT', 'ibSupport'];
// MSM role -> FCLM process_path (the name actuals are tracked under), for Slack function deltas.
// EOL Sort + AROS Sort both clock to IDRT, which FCLM reports as 'EOL Sort' (shown as IDRT Sort).
const FCLM_FN_ALIAS = {
  'Pallet Wrap/Lantech CPLH': 'Wrap/Lantech', 'Wall Builder': 'Offline Wallbuild', 'Sorter Wallbuilder': 'Sorter Wallbuild',
  'IB Pallet EPJ Waterspider': 'IB Pallet EPJ WS', 'IB PSolver': 'IB Problem Solve', 'OB PS': 'DA Problem Solve',
  'Re-inject': 'Trans Out Overflow', 'OB PIT OPERATOR': 'OB PIT Operator', 'AROS Sort': 'EOL Sort',
};
const fclmFnOf = (casual) => FCLM_FN_ALIAS[casual] || casual.replace(/ CPLH$/, '');
// Slack dept rows: MSM roles in each group + the carton key the group is rated on.
const SLACK_DEPTS = {
  pid: { label: 'PID Dept', depts: ['pid'], volKey: 'pid' },
  pr: { label: 'PR Dept', depts: ['ibPR'], volKey: 'pr' },
  sorterFluid: { label: 'Sorter Fluid', roles: ['Sorter Wallbuilder', 'Jam Clear'], volKey: 'fluidDirect' },
  offlineFluid: { label: 'Offline Fluid', roles: ['Wall Builder', 'Fluid Load', 'Fluid WS'], volKey: 'fluidOffline' },
  obpr: { label: 'OBPR', depts: ['obPR'], volKey: 'obPR' },
};
const DEFAULT_FPP = { pid: 0.5, aros: 0.07, pr: 0.35, skid: 0.08 }; // LVP defaults
const PATH_ANCHOR = { pid: 'PID Unload CPLH', pr: 'PR Unload CPLH', aros: 'AROS Unload', pit: 'PIT Unloader', fluid: 'Fluid Load', obpit: 'Pallet Loader CPLH' };

function ymd(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
const clampStep = (v, min, max, step) => { let x = Math.max(min, Math.min(max, v)); x = step === 2 ? Math.round(x / 2) * 2 : Math.round(x); return Math.max(min, Math.min(max, x)); };

export default function MSM({ api, site }) {
  const today = new Date();
  const [date, setDate] = useState(ymd(today));
  const [shiftType, setShiftType] = useState('day');
  const [lookbackWeeks, setLookbackWeeks] = useState(13);
  const [lvpHours, setLvpHours] = useState(0);
  const [hoursOverride, setHoursOverride] = useState('');
  const [shape, setShape] = useState(null);
  const [brtMin, setBrtMin] = useState(null); // avg BRT minutes per quarter
  const [brtFrac, setBrtFrac] = useState(0.875); // avg belt-running fraction of paid time
  const [daysUsed, setDaysUsed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [goals, setGoals] = useState({ pid: '', pr: '', aros: '', pit: '', fluid: '', obpit: '' });
  const [lvp, setLvp] = useState(null); // full LVP_<week> plan payload
  const [useHistShape, setUseHistShape] = useState(true); // true = historical FCLM labor shape (default); false = flat roster
  const [absorbSurplus, setAbsorbSurplus] = useState(true); // Option C: surplus HC -> extra path volume (<= 1.25x goal)
  const [lvpFnHours, setLvpFnHours] = useState({}); // computedHours[casual][shiftKey] from LVP
  const [lvpAdminHours, setLvpAdminHours] = useState({}); // adminHours[shiftKey] from LVP
  const [capOverride, setCapOverride] = useState({});
  const [heavyPallets, setHeavyPallets] = useState(false);
  const [highCageWIP, setHighCageWIP] = useState(false);
  const [highPiles, setHighPiles] = useState(false);
  const [qGoalOverride, setQGoalOverride] = useState({}); // `${pathKey}|${q}` -> cartons
  const [cellOverride, setCellOverride] = useState({});
  const [saved, setSaved] = useState(false);
  // Shift plans: everyone works the shared Live sheet; Prior-Day / Pre-Shift are saved
  // sign-off snapshots, viewable read-only from the View dropdown.
  const [viewKey, setViewKey] = useState('live'); // 'live' | '<kind>|<n>'
  const [viewMeta, setViewMeta] = useState(null);
  const [planIndex, setPlanIndex] = useState({ versions: [] });
  const [planMsg, setPlanMsg] = useState('');
  const [liveNonce, setLiveNonce] = useState(0);
  const viewRef = useRef('live');
  const isLive = viewKey === 'live';

  const d = new Date(date + 'T12:00:00');
  const dayName = DAYS[d.getDay()];
  const shiftKey = `${dayName}_${shiftType}`;

  useEffect(() => {
    const sunday = new Date(d); sunday.setDate(d.getDate() - d.getDay());
    const shiftKey = `${dayName}_${shiftType}`;
    fetch(`${api}/api/fclm/cache?site=${site}&date=LVP_${ymd(sunday)}&shift_type=plan`)
      .then(r => r.json())
      .then(lvp => {
        if (viewRef.current !== 'live') return; // a saved plan is on screen
        setLvp(lvp && typeof lvp === 'object' ? lvp : null);
        // Planned hours = sum of LVP dept totals (= LVP IB + DA). Falls back to the saved
        // plannedHours for plans saved before computedHours carried dept totals.
        const ch = lvp?.computedHours || {};
        const deptSum = LVP_DEPT_NAMES.reduce((t, n) => t + (parseFloat(ch[n]?.[shiftKey]) || 0), 0);
        const ibda = (lvp?.ibHours?.[shiftKey] || 0) + (lvp?.daHours?.[shiftKey] || 0);
        setLvpHours(deptSum || lvp?.plannedHours?.[shiftKey] || ibda || 0);
        // Path goals = LVP's own volumes (PR = total - PID - AROS; LVP plans SKID inside PR).
        const V = lvpVols(lvp?.shiftData?.[shiftKey], lvp?.fppRatios || DEFAULT_FPP, lvp?.eolPct !== undefined ? lvp.eolPct : 33);
        setGoals({ pid: V.pid || '', pr: V.pr || '', aros: V.aros || '', pit: '', fluid: V.fluidOffline || '', obpit: V.obPR || '' });
        setLvpFnHours(lvp?.computedHours || {});
        setLvpAdminHours(lvp?.adminHours || {});
        // Overlay any saved MSM plan for this date/shift (overrides the LVP-derived defaults)
        fetch(`${api}/api/fclm/cache?site=${site}&date=MSM_${date}&shift_type=${shiftType}`)
          .then(r => r.json())
          .then(m => {
            if (viewRef.current !== 'live') return;
            const s = m?.msm;
            if (s) {
              if (s.goals) setGoals(g0 => ({ ...g0, ...s.goals }));
              setCapOverride(s.capOverride || {});
              setQGoalOverride(s.qGoalOverride || {});
              setCellOverride(s.cellOverride || {});
              if (s.hoursOverride !== undefined) setHoursOverride(s.hoursOverride);
              setHeavyPallets(!!s.heavyPallets); setHighCageWIP(!!s.highCageWIP); setHighPiles(!!s.highPiles);
              if (s.useHistShape !== undefined) setUseHistShape(!!s.useHistShape);
              if (s.absorbSurplus !== undefined) setAbsorbSurplus(!!s.absorbSurplus);
            } else {
              setCapOverride({}); setQGoalOverride({}); setCellOverride({});
            }
          }).catch(() => {});
      }).catch(() => { setLvpHours(0); setLvp(null); });
  }, [api, site, date, shiftType, liveNonce]);

  useEffect(() => {
    setLoading(true);
    const q = QUARTERS.map(Q => ({ id: Q.id, labels: shiftType === 'day' ? Q.dayLabels : Q.nightLabels }));
    const dates = [];
    for (let w = 1; w <= lookbackWeeks; w++) { const hd = new Date(d); hd.setDate(d.getDate() - 7 * w); if (hd < new Date('2026-01-01T00:00:00')) break; dates.push(ymd(hd)); }
    Promise.all(dates.map(dt => fetch(`${api}/api/fclm/cache?site=${site}&date=${dt}&shift_type=${shiftType}`).then(r => r.json()).then(data => ({ data })).catch(() => ({ data: null }))))
      .then(results => {
        if (viewRef.current !== 'live') { setLoading(false); return; }
        const agg = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 }; let used = 0;
        const brtAgg = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 }; let brtDays = 0, sumBRT = 0, sumPaid = 0;
        results.forEach(({ data }) => {
          if (!data || !Array.isArray(data.labor)) return;
          const qh = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
          data.labor.forEach(l => { const hrs = l.hours || {}; q.forEach(Q => Q.labels.forEach(lbl => { qh[Q.id] += hrs[lbl] || 0; })); });
          const t = qh.Q1 + qh.Q2 + qh.Q3 + qh.Q4; if (t <= 0) return; used += 1; QIDS.forEach(k => agg[k] += qh[k] / t * 100);
          // BRT minutes per quarter from schedule[].brt aligned to hour_labels
          const labels = data.hour_labels || []; const sched = data.schedule || [];
          if (labels.length && sched.length) {
            const qb = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 }; let ok = false;
            labels.forEach((lbl, i) => { const brt = sched[i]?.brt || 0; const paid = sched[i]?.paid || 0; sumBRT += brt; sumPaid += paid; q.forEach(Q => { if (Q.labels.includes(lbl)) { qb[Q.id] += brt; ok = true; } }); });
            if (ok) { brtDays += 1; QIDS.forEach(k => brtAgg[k] += qb[k]); }
          }
        });
        setShape(used > 0 ? { Q1: agg.Q1 / used, Q2: agg.Q2 / used, Q3: agg.Q3 / used, Q4: agg.Q4 / used } : null);
        setBrtMin(brtDays > 0 ? { Q1: brtAgg.Q1 / brtDays, Q2: brtAgg.Q2 / brtDays, Q3: brtAgg.Q3 / brtDays, Q4: brtAgg.Q4 / brtDays } : null);
        setBrtFrac(sumPaid > 0 ? sumBRT / sumPaid : 0.875);
        setDaysUsed(used); setLoading(false);
      });
  }, [api, site, date, shiftType, lookbackWeeks, liveNonce]);

  // Changing date/shift always returns to Live and reloads that shift's plan index.
  useEffect(() => {
    viewRef.current = 'live'; setViewKey('live'); setViewMeta(null); setPlanMsg('');
    loadPlanIndex(api, site, date, shiftType).then(setPlanIndex);
  }, [api, site, date, shiftType]);

  const qPaidOf = (q) => QUARTERS.find(x => x.id === q).paidHrs;
  const adminHrsLvp = parseFloat(lvpAdminHours[shiftKey]) || 0; // LVP admin (Admin/HR/IT) hours -> OB Support 'Admin' row
  const lvpPlannedAll = lvpHours + adminHrsLvp; // LVP IB + DA + Admin = the HC the grid must staff
  const plannedTotal = hoursOverride !== '' ? (parseFloat(hoursOverride) || 0) : lvpPlannedAll;
  // Share of the shift's hours in each quarter. Historical (default): the FCLM labor shape
  // from the same weekday/shift over the lookback, so HC follows how the floor really staffs
  // (start-up ramp, end-of-shift drop). Flat roster: proportional to paid hours (same HC every quarter).
  const TOTAL_PAID = QUARTERS.reduce((t, Q) => t + Q.paidHrs, 0);
  const weight = (q) => useHistShape ? (shape ? (shape[q] || 0) / 100 : 0) : QUARTERS.find(x => x.id === q).paidHrs / TOTAL_PAID;
  const phase1 = QUARTERS.map(Q => { const w = weight(Q.id); const hrs = plannedTotal * w; return { id: Q.id, pct: w * 100, hrs, hc: Math.round(Q.paidHrs > 0 ? hrs / Q.paidHrs : 0) }; });
  const lvpExpected = parseFloat(lvp?.expectedHours?.[shiftKey]) || 0; // LVP rostered x attendance
  const capFor = (qid) => capOverride[qid] !== undefined && capOverride[qid] !== '' ? (parseInt(capOverride[qid]) || 0) : (phase1.find(p => p.id === qid)?.hc || 0);
  const capTotal = QIDS.reduce((s, q) => s + capFor(q), 0) || 1;
  const capShare = Object.fromEntries(QIDS.map(q => [q, capFor(q) / capTotal]));
  const g = (k) => parseFloat(goals[k]) || 0;
  // LVP volumes for this shift and per-role carton basis (same resolution as LVP getFnHours)
  const V = lvpVols(lvp?.shiftData?.[shiftKey], lvp?.fppRatios || DEFAULT_FPP, lvp?.eolPct !== undefined ? lvp.eolPct : 33);
  const cpp = parseFloat(lvp?.cpp) || 30.98;
  const DEPT_VOL_KEY = { pid: 'pid', aros: 'aros', ibPR: 'pr', ibPIT: 'pr', sorterEOL: 'eol', fluid: 'fluidTotal', obPR: 'obPR', ibSupport: 'ibCartons', obSupport: 'daCartons' };
  const DEPT_VOL = { pid: V.pid, aros: V.aros, ibPR: V.pr, ibPIT: V.pr, sorterEOL: V.eol, fluid: V.fluidTotal, obPR: V.obPR, ibSupport: V.ibCartons, obSupport: V.daCartons };
  const roleVol = (casual, deptKey) => {
    const r = LVP_ROLE[lvpCasualOf(casual)];
    if (!r) return DEPT_VOL[deptKey] || 0; // MSM-only role: dept volume
    if (r.fn === '_line_palletReceive') return (V.pr || 0) / cpp; // pallets, as LVP
    const vk = lvpResolveFnVolKey(r.fn, r.dept);
    return V[vk] || V[r.dept.volKey] || 0;
  };
  const lvpHoursRaw = (casual) => casual === 'Admin' ? adminHrsLvp : (parseFloat(lvpFnHours[lvpCasualOf(casual)]?.[shiftKey]) || 0);
  // Planned Hours override scales every LVP-driven function's hours (and so its HC) by
  // Planned ÷ LVP planned; volumes stay put, so path rates scale with it. Fixed-count roles don't change.
  const hoursScale = hoursOverride !== '' && lvpPlannedAll > 0 ? plannedTotal / lvpPlannedAll : 1;
  const lvpHoursFor = (casual) => lvpHoursRaw(casual) * hoursScale;
  // Path rate = LVP's rate for the path's anchor role (role volume / LVP role hours).
  const lvpPathRate = (k) => { const a = PATH_ANCHOR[k]; const h = lvpHoursFor(a); const v = roleVol(a); return h > 0 && v > 0 ? v / h : 0; };
  const DEF_RATE = { ...Object.fromEntries(PATHS.map(p => [p.key, p.defRate])), fluid: 150, obpit: 200 };
  const rateOf = (k) => lvpPathRate(k) || DEF_RATE[k] || 150;

  // Belt-running conversion: LVP rate is a PAID-hour CPLH over the 10h shift, but
  // the belt only runs BRT minutes. Active rate (per running hour) = LVP rate / BRT-fraction.
  const bf = brtFrac > 0 ? brtFrac : 0.875;
  const activeRate = (k) => rateOf(k) / bf;
  const qBrtHrs = (q) => (brtMin ? (brtMin[q] || 0) : (QUARTERS.find(x => x.id === q).paidHrs * 60 * bf)) / 60;
  // Cartons one associate can process in quarter q on a path = activeRate * quarter BRT hours
  const capPerAssoc = (k, q) => activeRate(k) * qBrtHrs(q);

  // AROS baseline: pack fixed-2 crew into the fewest high-capacity quarters to hit goal (±10%)
  const arosBaseline = {}; QIDS.forEach(q => arosBaseline[q] = 0);
  (() => {
    const goal = g('aros'); if (goal <= 0) return;
    const order = [...QIDS].sort((a, b) => capFor(b) - capFor(a)); let rem = goal;
    for (const q of order) { if (rem > goal * 0.1) { const prod = 2 * capPerAssoc('aros', q); arosBaseline[q] = prod; rem -= prod; } }
  })();

  // PIT/SKID baseline: PIT unload is a high-rate, low-headcount path — 1 AA in a
  // single quarter can clear the whole goal. Pack 1 AA into the fewest (highest-
  // capacity) quarters until the goal is met, scheduling min(1 AA capacity,
  // remaining goal) per quarter so we never over-plan past the goal.
  const pitBaseline = {}; QIDS.forEach(q => pitBaseline[q] = 0);
  (() => {
    const goal = g('pit'); if (goal <= 0) return;
    const order = [...QIDS].sort((a, b) => capFor(b) - capFor(a)); let rem = goal;
    for (const q of order) {
      if (rem <= goal * 0.05) break;
      const cap = capPerAssoc('pit', q);
      const prod = Math.min(cap, rem);
      pitBaseline[q] = prod; rem -= prod;
    }
  })();

  // Effective per-quarter cartons goal for a path (manual override wins)
  const perQuarterGoal = (pathKey, q) => {
    const ov = qGoalOverride[`${pathKey}|${q}`];
    if (ov !== undefined && ov !== '') return parseFloat(ov) || 0;
    if (pathKey === 'aros') return Math.round(arosBaseline[q]);
    if (pathKey === 'pit') return Math.round(pitBaseline[q]);
    if (pathKey === 'fluid') {
      let base = g('fluid') * capShare[q];
      if (highCageWIP && (q === 'Q1' || q === 'Q2')) { const fh = (capShare.Q1 + capShare.Q2) || 1; base += 1000 * (capShare[q] / fh); }
      return Math.round(base);
    }
    if (pathKey === 'obpit') return Math.round(g('obpit') / 4); // spread OBPR/PIT equally across quarters
    return Math.round(g(pathKey) * capShare[q]);
  };

  // Primary crews per quarter: crew = target cartons / cartons-per-associate (active rate x quarter BRT)
  const primaryCrew = {};
  PATHS.forEach(p => {
    primaryCrew[p.key] = {};
    QIDS.forEach(q => { const cap = capPerAssoc(p.key, q); const qGoal = perQuarterGoal(p.key, q); primaryCrew[p.key][q] = (cap > 0 && qGoal > 0) ? clampStep(qGoal / cap, p.min, p.max, p.crewStep) : 0; });
  });
  const arosRunning = (q) => (primaryCrew.aros?.[q] || 0) > 0;
  const fluidCrew = {}; QIDS.forEach(q => { const cap = capPerAssoc('fluid', q); const qGoal = perQuarterGoal('fluid', q); fluidCrew[q] = (cap > 0 && qGoal > 0) ? Math.round(qGoal / cap) : 0; });
  const obpitCrew = {}; QIDS.forEach(q => { const cap = capPerAssoc('obpit', q); const qGoal = perQuarterGoal('obpit', q); obpitCrew[q] = (cap > 0 && qGoal > 0) ? Math.round(qGoal / cap) : 0; });

  // --- Option C: absorb surplus committed HC onto the DEMAND PATHS as extra
  // crew, raising their volume to LVP rate, HARD-CAPPED at CEIL_MULT x the
  // path's FPP-implied goal so volume can never run away (the earlier version
  // blew up by converting ALL surplus HC — incl. support roles — into volume
  // with no ceiling). Only carton-producing paths that are ALREADY running this
  // quarter receive crew (concentration preserved); support/indirect HC is NOT
  // converted to volume.
  const CEIL_MULT = 1.25;
  const CARTON_PATHS = [...PATHS.map(p => p.key), 'fluid', 'obpit'];
  const baseCrewOf = (k, q) => k === 'fluid' ? (fluidCrew[q] || 0) : k === 'obpit' ? (obpitCrew[q] || 0) : (primaryCrew[k]?.[q] || 0);
  const extraCrew = {}; const fillVolume = {};
  CARTON_PATHS.forEach(k => { extraCrew[k] = {}; fillVolume[k] = {}; QIDS.forEach(q => { extraCrew[k][q] = 0; fillVolume[k][q] = 0; }); });
  // NOTE: this runs AFTER `base` HC is computed below — see the deferred block.

  // Per-quarter scheduled cartons = (base crew + absorbed extra crew) x capacity,
  // hard-capped at CEIL_MULT x the path's FPP goal (ceiling applied in the fill
  // population below, baked into fillVolume). schedCartons = base + uplift.
  const schedCartons = (pathKey, q) => {
    const uplift = (fillVolume[pathKey] && fillVolume[pathKey][q]) || 0;
    if (pathKey === 'fluid') return Math.round((fluidCrew[q] || 0) * capPerAssoc('fluid', q)) + uplift;
    if (pathKey === 'obpit') return Math.round((obpitCrew[q] || 0) * capPerAssoc('obpit', q)) + uplift;
    // PIT: 1 AA's capacity far exceeds the goal, so cap scheduled volume at the
    // per-quarter goal (the packed baseline or a manual override) — staff 1 AA
    // for as long as needed rather than over-plan a full quarter of capacity.
    if (pathKey === 'pit') {
      const crew = primaryCrew.pit?.[q] || 0;
      if (crew <= 0) return 0;
      return Math.round(Math.min(crew * capPerAssoc('pit', q), perQuarterGoal('pit', q))) + uplift;
    }
    return Math.round((primaryCrew[pathKey]?.[q] || 0) * capPerAssoc(pathKey, q)) + uplift;
  };

  // HC implied by LVP planned hours for a role, spread across quarters by `weight` and
  // divided by the quarter's paid hours.
  const lvpHC = (casual, q) => {
    const H = lvpHoursFor(casual); if (!H) return 0;
    const clock = QUARTERS.find(x => x.id === q).paidHrs || 1;
    return H * weight(q) / clock;
  };
  // Whole-HC per quarter for an LVP-driven role, rounded so the role's total hours land as
  // close as possible to its LVP hours (per-quarter rounding zeroed small roles, e.g. 3h -> 0).
  // Ties (equal remainders, e.g. a flat roster) go to the least-loaded quarter so small
  // roles spread out instead of all landing in Q1. Load is seeded lazily with the demand crews.
  const lvpCountCache = {}; let lvpLoad = null;
  const lvpCounts = (casual) => {
    if (lvpCountCache[casual]) return lvpCountCache[casual];
    if (!lvpLoad) lvpLoad = Object.fromEntries(QIDS.map(q => [q, PATHS.reduce((t, p) => t + (primaryCrew[p.key]?.[q] || 0), 0) + (fluidCrew[q] || 0)]));
    const out = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 }; const H = lvpHoursFor(casual);
    if (H > 0) {
      const fr = QIDS.map(q => ({ q, x: lvpHC(casual, q) }));
      fr.forEach(f => { out[f.q] = Math.floor(f.x); });
      let hrs = QIDS.reduce((t, q) => t + out[q] * qPaidOf(q), 0);
      const rem = (f) => f.x - Math.floor(f.x);
      [...fr].sort((a, b) => (Math.abs(rem(b) - rem(a)) > 1e-9 ? rem(b) - rem(a) : (lvpLoad[a.q] + out[a.q]) - (lvpLoad[b.q] + out[b.q]))).forEach(f => {
        const p = qPaidOf(f.q); if (Math.abs(H - (hrs + p)) < Math.abs(H - hrs)) { out[f.q] += 1; hrs += p; }
      });
      QIDS.forEach(q => { lvpLoad[q] += out[q]; });
    }
    lvpCountCache[casual] = out; return out;
  };
  const lvpCount = (casual, q) => lvpCounts(casual)[q] || 0;
  const prRunning = (q) => (primaryCrew.pr?.[q] || 0) > 0;

  const autoCount = (casual, q, ctx) => {
    const r = RULE[casual];
    if (!r) return lvpCount(casual, q); // blank/manual roles: staff from LVP planned hours
    switch (r.type) {
      case 'primary': return primaryCrew[r.path]?.[q] || 0;
      case 'lvp': return lvpCount(casual, q);
      case 'fixed': return r.count || 0;
      case 'psolve': { const need = highPiles ? 4 : 2; const others = lvpCount('LP Receive', q) + lvpCount('Each Receive', q) + lvpCount('Prep Receive', q); return Math.max(need - others, lvpCount(casual, q), 0); } // LVP IB Problem Solve hours are the floor
      case 'arosOn': return arosRunning(q) ? (r.count || 0) : 0;
      case 'arosRange': return arosRunning(q) ? clampStep(lvpCount(casual, q) || r.min, r.min, r.max, 1) : 0;
      case 'range': return clampStep(lvpCount(casual, q) || r.min, r.min, r.max, 1);
      case 'arosMin': return arosRunning(q) ? (r.count || 0) : 0;
      case 'arosLvp': return arosRunning(q) ? lvpCount(casual, q) : 0;
      case 'demandFluid': return fluidCrew[q] || 0;
      case 'prOn': return prRunning(q) ? clampStep(Math.max(r.count || 1, lvpCount(casual, q)), r.count || 1, r.max || 3, 1) : 0;
      case 'flexPath': { const h = lvpCount(casual, q); if (h > 0) return clampStep(h, r.min || 0, r.max || 99, 1); const pc = primaryCrew[r.path]?.[q] || 0; return (pc > 0 || r.slack) ? (r.min || 0) : 0; }
      case 'ratioStep': { const a = primaryCrew[r.anchor]?.[q] || 0; let v = 0; (r.steps || []).forEach(([thr, val]) => { if (a >= thr) v = val; }); return v; }
      case 'ratioMul': { const a = r.anchor === 'fluid' ? (fluidCrew[q] || 0) : (primaryCrew[r.anchor]?.[q] || 0); return a > 0 ? clampStep(a * r.mul, r.min, r.max, 1) : 0; }
      case 'condGt': { const a = ctx[`${r.anchorCasual}|${q}`] || 0; return a > r.thresh ? (r.count || 0) : 0; }
      case 'heavyOff': return heavyPallets ? 0 : (r.min || 0);
      case 'heavyOn': return heavyPallets ? (r.min || 0) : 0;
      case 'mirror': return autoCount(r.ofCasual, q, ctx);
      default: return lvpCount(casual, q);
    }
  };
  const byCasualQ = {};
  DEPTS.forEach(dep => dep.functions.forEach(f => QIDS.forEach(q => { const r = RULE[f.casual]; if (!r || r.type !== 'condGt') byCasualQ[`${f.casual}|${q}`] = autoCount(f.casual, q, {}); })));

  const cellKey = (dep, f, q) => `${dep.key}|${f.casual}|${q}`;
  // If a path's quarter goal is explicitly overridden to 0, zero out that dept's roles
  // for the quarter (fill-to-cap then redistributes the freed HC to other paths).
  const deptPathZero = { ibPR: 'pr', aros: 'aros', ibPIT: 'pit' };
  const explicitZero = (pathKey, q) => { const ov = qGoalOverride[`${pathKey}|${q}`]; return ov !== undefined && ov !== '' && (parseFloat(ov) || 0) === 0; };
  // Base counts (before fill-to-cap)
  const base = {};
  DEPTS.forEach(dep => dep.functions.forEach(f => QIDS.forEach(q => {
    let c = autoCount(f.casual, q, byCasualQ);
    const zp = deptPathZero[dep.key];
    if (zp && explicitZero(zp, q)) c = 0;
    base[cellKey(dep, f, q)] = c;
  })));
  // --- Option C fill population (runs now that `base` HC exists) -------------
  // Per quarter: surplus committed HC = capFor - rule-based HC. Distribute that
  // surplus ONLY onto carton paths already running this quarter, by FPP/goal
  // share, as extra crew. Each path's resulting volume = (baseCrew+extra) x cap,
  // HARD-CAPPED at CEIL_MULT x its FPP goal; extra crew is trimmed to whatever
  // the capped volume supports, so HC and volume stay consistent and bounded.
  // Any surplus HC that can't be placed within the ceiling is left UNPLACED
  // (honest signal that volume can't justify the committed heads).
  const pathAnchorCasual = { pid: 'PID Unload CPLH', pr: 'PR Unload CPLH', aros: 'AROS Unload', pit: 'PIT Unloader', fluid: 'Fluid Load', obpit: 'Pallet Loader CPLH' };
  const fppGoalOf = (k) => g(k) || 0;   // path's FPP-implied daily goal (carton goal)
  const bucketExtra = {};                 // cellKey -> HC added to support roles (stage 2)
  const fasterCrew = {}; CARTON_PATHS.forEach(k => { fasterCrew[k] = {}; }); // stage-3 crew per path/quarter
  QIDS.forEach(q => {
    const baseTotalHC = DEPTS.reduce((s, dep) => s + dep.functions.reduce((a, f) => a + (base[cellKey(dep, f, q)] || 0), 0), 0);
    let surplusHC = capFor(q) - baseTotalHC;
    if (!absorbSurplus || surplusHC <= 0) return;
    const running = CARTON_PATHS.filter(k => baseCrewOf(k, q) > 0 && fppGoalOf(k) > 0);
    let shareSum = running.reduce((s, k) => s + fppGoalOf(k), 0);
    if (shareSum <= 0) return;
    running.forEach(k => {
      const cap = capPerAssoc(k, q); if (cap <= 0) return;
      const wantHC = surplusHC * (fppGoalOf(k) / shareSum);
      // Volume ceiling for this path, prorated to the quarter by capacity share.
      const dailyCeil = CEIL_MULT * fppGoalOf(k);
      const qGoalCeil = dailyCeil * (capShare[q] || 0);               // this quarter's slice of the 1.25x ceiling
      const baseVol = baseCrewOf(k, q) * cap;
      const headroom = Math.max(0, qGoalCeil - baseVol);              // cartons we may still add this quarter
      const addVol = Math.min(wantHC * cap, headroom);                // bounded by the ceiling
      if (addVol <= 0) return;
      fillVolume[k][q] = Math.round(addVol);
      extraCrew[k][q] = Math.round(addVol / cap);                     // crew consistent with the (capped) volume
    });
    let left = surplusHC - running.reduce((t, k) => t + (extraCrew[k][q] || 0), 0);
    // Stage 2 - over buckets: true up rule-capped support roles toward their LVP HC
    // (up to the rule max), biggest shortfall first. AROS-gated roles only while AROS runs.
    if (left > 0) {
      const gaps = [];
      DEPTS.forEach(dep => dep.functions.forEach(f => {
        const r = RULE[f.casual]; if (!r || r.max === undefined || r.type === 'primary' || r.type === 'demandFluid') return;
        const k = cellKey(dep, f, q); const b = base[k] || 0;
        if (b === 0 && /^aros/.test(r.type)) return;
        if (cellOverride[k] !== undefined && cellOverride[k] !== '') return;
        const gap = Math.min(r.max, Math.max(lvpCount(f.casual, q), b)) - b;
        if (gap > 0) gaps.push({ k, gap });
      }));
      gaps.sort((a, b) => b.gap - a.gap).forEach(({ k, gap }) => { if (left <= 0) return; const add = Math.min(gap, left); bucketExtra[k] = (bucketExtra[k] || 0) + add; left -= add; });
    }
    // Stage 3 - flow faster: anything still unplaced goes onto the running paths above the
    // 1.25x ceiling, split by goal share, so the quarter fills to its cap.
    if (left > 0) {
      const shares = running.map(k => ({ k, x: left * fppGoalOf(k) / shareSum }));
      shares.forEach(o => { o.n = Math.floor(o.x); });
      let rem = left - shares.reduce((t, o) => t + o.n, 0);
      [...shares].sort((a, b) => (b.x - b.n) - (a.x - a.n)).forEach(o => { if (rem > 0) { o.n += 1; rem -= 1; } });
      shares.forEach(({ k, n }) => {
        if (n <= 0) return; const cap = capPerAssoc(k, q);
        extraCrew[k][q] = (extraCrew[k][q] || 0) + n; fasterCrew[k][q] = n;
        fillVolume[k][q] = (fillVolume[k][q] || 0) + Math.round(n * cap);
      });
    }
  });
  // Display helper: extra crew to show on a path's anchor role cell.
  const extraForCell = (dep, f, q) => {
    const path = Object.keys(pathAnchorCasual).find(k => pathAnchorCasual[k] === f.casual);
    return path ? (extraCrew[path]?.[q] || 0) : 0;
  };
  const val = (dep, f, q) => { const k = cellKey(dep, f, q); if (cellOverride[k] !== undefined && cellOverride[k] !== '') return parseInt(cellOverride[k]) || 0; return (base[k] || 0) + extraForCell(dep, f, q) + (bucketExtra[k] || 0); };
  const totalHC = Object.fromEntries(QIDS.map(q => [q, DEPTS.reduce((s, dep) => s + dep.functions.reduce((a, f) => a + val(dep, f, q), 0), 0)]));

  // Columns: total HC, computed hours, LVP hours, and cartons-per-path for rate columns
  const qPaid = (q) => QUARTERS.find(x => x.id === q).paidHrs;
  const roleTotalHC = (dep, f) => QIDS.reduce((s, q) => s + val(dep, f, q), 0);
  const roleHours = (dep, f) => QIDS.reduce((s, q) => s + val(dep, f, q) * qPaid(q), 0);
  // Dept Goals rate columns: path labor hours + carton rollups from the allocation grid
  const crewOf = (k, q) => k === 'fluid' ? (fluidCrew[q] || 0) : k === 'obpit' ? (obpitCrew[q] || 0) : (primaryCrew[k]?.[q] || 0);
  const pathHours = (k) => QIDS.reduce((s, q) => s + crewOf(k, q) * qPaid(q), 0);
  const gridDeptHours = (key) => { const d = DEPTS.find(x => x.key === key); return d ? d.functions.reduce((a, f) => a + roleHours(d, f), 0) : 0; };
  // IB / OB split matches LVP: IB depts + AROS IB roles + both IDRT lines; everything else OB.
  const roleIsIB = (dep, f) => IB_DEPT_KEYS.includes(dep.key) || IB_EXTRA_ROLES.has(f.casual);
  const roleIsAdmin = (f) => f.casual === 'Admin';
  const sideGridHours = (ib) => DEPTS.reduce((t, dep) => t + dep.functions.reduce((a, f) => a + (!roleIsAdmin(f) && roleIsIB(dep, f) === ib ? roleHours(dep, f) : 0), 0), 0);
  const adminGridHours = DEPTS.reduce((t, dep) => t + dep.functions.reduce((a, f) => a + (roleIsAdmin(f) ? roleHours(dep, f) : 0), 0), 0);
  const ibGridHours = sideGridHours(true);
  const obGridHours = sideGridHours(false);
  // LVP comparison figures for this shift
  const lvpCH = lvp?.computedHours || {};
  const lvpDeptHrs = (name) => parseFloat(lvpCH[name]?.[shiftKey]) || 0;
  const lvpTotalHrs = LVP_DEPT_NAMES.reduce((t, n) => t + lvpDeptHrs(n), 0);
  const lvpIBHrs = parseFloat(lvp?.ibHours?.[shiftKey]) || 0;
  const lvpOBHrs = lvpTotalHrs > 0 ? lvpTotalHrs - lvpIBHrs : 0;
  const LVP_DEPT_OF_PATH = { pid: 'PID', pr: 'IB PR', aros: 'AROS', pit: 'IB PIT', fluid: 'Fluid', obpit: 'OBPR' };
  // Dept Goals path row -> allocation-grid dept (Total Hrs = ALL roles in that dept)
  const pathGridDept = { pid: 'pid', pr: 'ibPR', aros: 'aros', pit: 'ibPIT', fluid: 'fluid', obpit: 'obPR' };

  const savePlan = async () => {
    try {
      await fetch(`${api}/api/fclm/cache`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site, date: `MSM_${date}`, shift_type: shiftType, data: { msm: { goals, capOverride, qGoalOverride, cellOverride, hoursOverride, heavyPallets, highCageWIP, highPiles, useHistShape, absorbSurplus } } }),
      });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { /* ignore */ }
  };

  // ---- Prior-Day / Pre-Shift plan snapshots ----
  const shiftOnly = (o) => (o && o[shiftKey] !== undefined ? { [shiftKey]: o[shiftKey] } : {});
  const buildSnapshot = () => {
    const ibQ = Object.fromEntries(QIDS.map(q => [q, ['pid', 'pr', 'aros', 'pit'].reduce((t, k) => t + schedCartons(k, q), 0)]));
    const ibAll = QIDS.reduce((t, q) => t + ibQ[q], 0) || 1;
    const quarters = Object.fromEntries(QIDS.map(q => {
      let ib = 0, ob = 0, adm = 0;
      DEPTS.forEach(dep => dep.functions.forEach(f => { const h = val(dep, f, q) * qPaid(q); if (roleIsAdmin(f)) adm += h; else if (roleIsIB(dep, f)) ib += h; else ob += h; }));
      const obC = ibQ[q] + (highCageWIP ? 1000 * ibQ[q] / ibAll : 0);
      return [q, { hc: totalHC[q], hours: Math.round(ib + ob + adm), ibHours: Math.round(ib), obHours: Math.round(ob), adminHours: Math.round(adm), ibCartons: Math.round(ibQ[q]), cartons: Math.round(ibQ[q] + obC) }];
    }));
    // Per-quarter planned volumes by carton key, function hours (by FCLM name) and Slack dept hours
    const e = (lvp?.eolPct !== undefined ? lvp.eolPct : 33) / 100;
    QIDS.forEach(q => {
      const pid = schedCartons('pid', q), pr = schedCartons('pr', q), aros = schedCartons('aros', q), eol = Math.round(pid * e);
      const obC = quarters[q].cartons - quarters[q].ibCartons;
      quarters[q].vols = { pid, pr, aros, eol, fluidDirect: pid - eol, fluidOffline: eol + aros, fluidTotal: pid + aros, eolAros: eol + aros,
        ibCartons: quarters[q].ibCartons, daCartons: Math.round(obC), obPR: pr, skid: pr, pallets: Math.round(pr / cpp) };
      const fn = {};
      DEPTS.forEach(dep => dep.functions.forEach(f => { if (roleIsAdmin(f)) return; const h = val(dep, f, q) * qPaid(q); if (h > 0) { const n = fclmFnOf(f.casual); fn[n] = Math.round(((fn[n] || 0) + h) * 10) / 10; } }));
      quarters[q].fn = fn;
      quarters[q].dept = Object.fromEntries(Object.entries(SLACK_DEPTS).map(([key, sd]) => [key, Math.round(DEPTS.reduce((t, dep) => t + dep.functions.reduce((a, f) => a + (((sd.depts || []).includes(dep.key) || (sd.roles || []).includes(f.casual)) ? val(dep, f, q) * qPaid(q) : 0), 0), 0) * 10) / 10]));
    });
    // Carton key each FCLM function is rated on (LVP basis; IDRT Sort = EOL + AROS)
    const fnVolKey = {};
    DEPTS.forEach(dep => dep.functions.forEach(f => {
      if (roleIsAdmin(f)) return; const n = fclmFnOf(f.casual);
      if (n === 'EOL Sort') { fnVolKey[n] = 'eolAros'; return; }
      const r = LVP_ROLE[lvpCasualOf(f.casual)];
      fnVolKey[n] = r ? (r.fn === '_line_palletReceive' ? 'pallets' : (lvpResolveFnVolKey(r.fn, r.dept) || r.dept.volKey)) : (DEPT_VOL_KEY[dep.key] || 'ibCartons');
    }));
    const lvpIB = (V.pid || 0) + (V.pr || 0) + (V.aros || 0);
    const lvpCmp = { hours: Math.round(lvpPlannedAll), cartons: (V.ibCartons || 0) * 2, ibCartons: V.ibCartons || 0,
      fpp: lvpIB > 0 ? { pid: V.pid / lvpIB, pr: V.pr / lvpIB, aros: V.aros / lvpIB } : null };
    const tot = QIDS.reduce((t, q) => ({ hours: t.hours + quarters[q].hours, cartons: t.cartons + quarters[q].cartons }), { hours: 0, cartons: 0 });
    return {
      savedBy: (getUser() || {}).username || '',
      inputs: { goals, capOverride, qGoalOverride, cellOverride, hoursOverride, heavyPallets, highCageWIP, highPiles, useHistShape, absorbSurplus, lookbackWeeks },
      pinned: {
        lvpHours, shape, brtMin, brtFrac, daysUsed,
        lvp: lvp ? { shiftData: shiftOnly(lvp.shiftData), fppRatios: lvp.fppRatios, eolPct: lvp.eolPct, cpp: lvp.cpp,
          computedHours: Object.fromEntries(Object.entries(lvp.computedHours || {}).map(([k, v]) => [k, shiftOnly(v)])),
          ibHours: shiftOnly(lvp.ibHours), daHours: shiftOnly(lvp.daHours), plannedHours: shiftOnly(lvp.plannedHours),
          adminHours: shiftOnly(lvp.adminHours), expectedHours: shiftOnly(lvp.expectedHours) } : null,
      },
      summary: { site, date, shift: shiftType, quarters, fnVolKey, slackDepts: Object.fromEntries(Object.entries(SLACK_DEPTS).map(([k, v]) => [k, { label: v.label, volKey: v.volKey }])), lvp: lvpCmp, hours: tot.hours, cartons: tot.cartons, rate: tot.hours > 0 ? Math.round(tot.cartons / tot.hours * 10) / 10 : 0, peakHC: Math.max(...QIDS.map(q => totalHC[q])) },
    };
  };
  const savePlanKind = async (kind) => {
    if (!isLive) return;
    const label = PLAN_KINDS[kind];
    const idx = await loadPlanIndex(api, site, date, shiftType); // fresh: someone else may have saved
    if (idx.versions.some(v => v.kind === kind) && !window.confirm(`Overwrite existing ${label} Plan?`)) return;
    setPlanMsg(`Saving ${label} plan…`);
    try {
      await savePlan(); // the Live sheet becomes what was signed off
      const meta = await savePlanVersion(api, site, date, shiftType, kind, buildSnapshot());
      setPlanIndex(await loadPlanIndex(api, site, date, shiftType));
      setPlanMsg(`✅ ${label} plan saved (v${meta.n})`);
    } catch (e) { setPlanMsg(`❌ ${label} plan not saved: ${e.message}`); }
  };
  const openView = async (key) => {
    setPlanMsg('');
    if (key === 'live') { viewRef.current = 'live'; setViewKey('live'); setViewMeta(null); setLiveNonce(n => n + 1); return; }
    const [kind, n] = key.split('|');
    const v = await loadPlanVersion(api, site, date, shiftType, kind, parseInt(n));
    if (!v) { setPlanMsg('❌ Could not load that plan'); return; }
    viewRef.current = key; setViewKey(key); setViewMeta(v);
    const I = v.inputs || {}, P = v.pinned || {};
    setGoals(I.goals || {}); setCapOverride(I.capOverride || {}); setQGoalOverride(I.qGoalOverride || {}); setCellOverride(I.cellOverride || {});
    setHoursOverride(I.hoursOverride !== undefined ? I.hoursOverride : ''); setHeavyPallets(!!I.heavyPallets); setHighCageWIP(!!I.highCageWIP); setHighPiles(!!I.highPiles);
    setUseHistShape(I.useHistShape !== false); setAbsorbSurplus(I.absorbSurplus !== false);
    setLvp(P.lvp || null); setLvpHours(P.lvpHours || 0); setLvpFnHours((P.lvp && P.lvp.computedHours) || {}); setLvpAdminHours((P.lvp && P.lvp.adminHours) || {});
    setShape(P.shape || null); setBrtMin(P.brtMin || null); setBrtFrac(P.brtFrac || 0.875); setDaysUsed(P.daysUsed || 0); setLoading(false);
  };
  const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { return iso; } };
  const latestOf = (kind) => planIndex[kind];

  const cell = { padding: '3px 6px', border: '1px solid #ccc', textAlign: 'center', fontSize: '0.72rem' };
  const hdr = { ...cell, background: '#333', color: '#fff', fontWeight: 600 };
  const inp = { width: 42, textAlign: 'center', fontSize: '0.72rem', border: '1px solid #ddd' };

  return (
    <div>
      {/* ===== SHIFT PLAN BAR: Live front-sheet + Prior-Day / Pre-Shift sign-off ===== */}
      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.8rem', padding: '10px 14px' }}>
        <label>Date: <input type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
        <span style={{ fontWeight: 600 }}>{dayName}</span>
        {['day', 'night'].map(s => <button key={s} className={`btn ${shiftType === s ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShiftType(s)} style={{ fontSize: '0.75rem', padding: '4px 10px' }}>{s === 'day' ? 'Day' : 'Night'}</button>)}
        <label>View: <select value={viewKey} onChange={e => openView(e.target.value)} aria-label="Plan view">
          <option value="live">Live</option>
          {[...planIndex.versions].reverse().map(v => <option key={`${v.kind}|${v.n}`} value={`${v.kind}|${v.n}`}>{PLAN_KINDS[v.kind] || v.kind} v{v.n} — {fmtWhen(v.savedAt)}{v.savedBy ? ` (${v.savedBy})` : ''}</option>)}
        </select></label>
        {isLive
          ? <span style={{ background: '#2e7d32', color: '#fff', fontWeight: 700, borderRadius: 4, padding: '2px 10px', letterSpacing: 1 }}>LIVE</span>
          : <span style={{ background: '#ef6c00', color: '#fff', fontWeight: 700, borderRadius: 4, padding: '2px 10px' }}>VIEWING {(PLAN_KINDS[viewMeta?.kind] || '').toUpperCase()} PLAN v{viewMeta?.n} · {fmtWhen(viewMeta?.savedAt)}{viewMeta?.savedBy ? ` · ${viewMeta.savedBy}` : ''} · read-only</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          {['priorDay', 'preShift'].map(kind => { const l = latestOf(kind); return (
            <button key={kind} className="btn btn-secondary" disabled={!isLive} onClick={() => savePlanKind(kind)} title={l ? `Latest: v${l.n} ${fmtWhen(l.savedAt)}${l.savedBy ? ` by ${l.savedBy}` : ''}` : 'Not saved yet'} style={{ fontSize: '0.72rem', padding: '4px 10px' }}>
              💾 Save {PLAN_KINDS[kind]} Plan{l ? ` (v${l.n})` : ''}
            </button>); })}
        </span>
        {planMsg && <span style={{ width: '100%', fontSize: '0.72rem', color: planMsg.startsWith('❌') ? '#c62828' : '#2e7d32' }}>{planMsg}</span>}
      </div>

      <fieldset disabled={!isLive} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {/* ===== PHASE 1: forecast + dept goals ===== */}
      <div className="card">
        <h2 className="section-title">📊 Max Staffing Model — Quarterly HC Forecast</h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, fontSize: '0.8rem' }}>
          <label>Lookback: <select value={lookbackWeeks} onChange={e => setLookbackWeeks(parseInt(e.target.value))}><option value={8}>8</option><option value={13}>13</option><option value={26}>26</option><option value={52}>52</option></select> wks</label>
          <label title="LVP IB + DA + Admin hours">Planned Hours (LVP): <input type="number" value={hoursOverride} placeholder={lvpPlannedAll ? String(Math.round(lvpPlannedAll)) : '—'} onChange={e => setHoursOverride(e.target.value)} style={{ width: 90, textAlign: 'center' }} />
            {hoursScale !== 1 && <span style={{ marginLeft: 6, color: '#1565c0' }} title="Every LVP-based function's hours/HC are scaled by Planned ÷ LVP planned">× {hoursScale.toFixed(2)} vs LVP</span>}</label>
          <label title="On (default) = spread hours by the historical FCLM labor shape. Off = flat roster: same HC every quarter (hours / 10 paid hrs)."><input type="checkbox" checked={useHistShape} onChange={e => setUseHistShape(e.target.checked)} /> Historical shape</label>
          <label title="On: spare HC under the cap is added to running demand paths as extra volume (capped at 1.25x the LVP goal). Off: Planned cartons stay at the LVP goal."><input type="checkbox" checked={absorbSurplus} onChange={e => setAbsorbSurplus(e.target.checked)} /> Absorb surplus HC</label>
          <label><input type="checkbox" checked={heavyPallets} onChange={e => setHeavyPallets(e.target.checked)} /> Heavy Pallets</label>
          <label><input type="checkbox" checked={highCageWIP} onChange={e => setHighCageWIP(e.target.checked)} /> High Cage WIP</label>
          <label><input type="checkbox" checked={highPiles} onChange={e => setHighPiles(e.target.checked)} /> High Piles</label>
        </div>
        <div style={{ fontSize: '0.72rem', color: '#666', marginBottom: 8 }}>{!useHistShape ? `Flat roster: hours spread by paid hours per quarter (HC = ${Math.round(plannedTotal)} h ÷ ${TOTAL_PAID} paid h).` : loading ? 'Loading history…' : shape ? `Historical shape from ${daysUsed} ${dayName} ${shiftType} shift(s).` : `No historical ${dayName} ${shiftType} data.`}</div>
        <table style={{ borderCollapse: 'collapse', maxWidth: 760, marginBottom: 14 }}>
          <thead><tr><th style={{ ...hdr, textAlign: 'left' }}>&nbsp;</th>{QUARTERS.map(Q => <th key={Q.id} style={hdr}>{Q.id}<div style={{ fontWeight: 400, fontSize: '0.58rem' }}>{shiftType === 'day' ? Q.dayWindow : Q.nightWindow}</div></th>)}<th style={hdr}>Total</th></tr></thead>
          <tbody>
            <tr><td style={{ ...cell, textAlign: 'left' }}>Shape %</td>{phase1.map(p => <td key={p.id} style={cell}>{p.pct ? p.pct.toFixed(1) + '%' : '—'}</td>)}<td style={cell}>100%</td></tr>
            <tr><td style={{ ...cell, textAlign: 'left' }}>Planned Hrs</td>{phase1.map(p => <td key={p.id} style={cell}>{p.hrs ? Math.round(p.hrs) : '—'}</td>)}<td style={cell}>{Math.round(plannedTotal)}</td></tr>
            <tr><td style={{ ...cell, textAlign: 'left', fontWeight: 700 }}>Concurrent HC</td>{phase1.map(p => <td key={p.id} style={{ ...cell, fontWeight: 700, background: '#e3f2fd' }}>{p.hc || '—'}</td>)}<td style={{ ...cell, fontWeight: 700 }}>peak {Math.max(...phase1.map(p => p.hc))}</td></tr>
            <tr><td style={{ ...cell, textAlign: 'left', color: '#666' }} title="LVP Expected = rostered hours x attendance">LVP Expected HC</td>{QUARTERS.map(Q => <td key={Q.id} style={{ ...cell, color: '#666' }}>{lvpExpected ? Math.round(lvpExpected * weight(Q.id) / Q.paidHrs) : '—'}</td>)}<td style={{ ...cell, color: '#666' }}>{lvpExpected ? `${Math.round(lvpExpected)} h` : '—'}</td></tr>
            <tr><td style={{ ...cell, textAlign: 'left' }}>BRT (min)</td>{QIDS.map(q => <td key={q} style={cell}>{brtMin ? Math.round(brtMin[q]) : '—'}</td>)}<td style={cell}>{brtMin ? Math.round(QIDS.reduce((s, q) => s + brtMin[q], 0)) : '—'}{brtMin ? ` (${Math.round(bf * 100)}%)` : ''}</td></tr>
          </tbody>
        </table>

        <h3 className="section-title" style={{ fontSize: '0.9rem' }}>Dept Goals by Quarter</h3>
        <table style={{ borderCollapse: 'collapse', maxWidth: 980 }}>
          <thead><tr>
            <th style={{ ...hdr, textAlign: 'left' }}>Path</th>
            {QIDS.map(q => <th key={q} style={hdr}>{q}</th>)}
            <th style={hdr}>Planned</th><th style={hdr}>LVP Goal</th><th style={hdr}>Total Hrs</th><th style={hdr}>Rate<div style={{ fontWeight: 400, fontSize: '0.55rem' }}>CPLH</div></th><th style={hdr}>LVP Rate<div style={{ fontWeight: 400, fontSize: '0.55rem' }}>goal ÷ LVP hrs</div></th>
          </tr></thead>
          <tbody>
            {[...PATHS.map(p => ({ key: p.key, label: p.label })), { key: 'fluid', label: 'Fluid-Offline' }, { key: 'obpit', label: 'OBPR / PIT' }].map(row => {
              const planned = QIDS.reduce((s, q) => s + schedCartons(row.key, q), 0);
              const goal = g(row.key); const within = goal > 0 ? Math.abs(planned - goal) / goal <= 0.1 : true;
              const hrs = gridDeptHours(pathGridDept[row.key] || ''); const rate = hrs > 0 ? planned / hrs : 0;
              return (
              <tr key={row.key}>
                <td style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>{row.label}</td>
                {QIDS.map(q => { const k = `${row.key}|${q}`; const ov = qGoalOverride[k] !== undefined && qGoalOverride[k] !== ''; const prod = schedCartons(row.key, q); return (
                  <td key={q} style={{ ...cell, padding: 1, background: ov ? '#fff3e0' : 'transparent' }}>
                    <input type="number" value={ov ? qGoalOverride[k] : ''} placeholder={prod ? String(prod) : '0'} onChange={e => setQGoalOverride({ ...qGoalOverride, [k]: e.target.value })} style={{ ...inp, width: 72 }} />
                  </td>); })}
                <td style={{ ...cell, fontWeight: 600, background: within ? '#c8e6c9' : '#ffe0b2' }}>{planned ? planned.toLocaleString() : '—'}</td>
                <td style={{ ...cell, padding: 1 }}><input type="number" value={goals[row.key]} onChange={e => setGoals({ ...goals, [row.key]: e.target.value })} style={{ ...inp, width: 80 }} /></td>
                <td style={cell}>{hrs ? Math.round(hrs) : '—'}</td>
                <td style={cell}>{rate ? rate.toFixed(1) : '—'}</td>
                {(() => { const lh = lvpDeptHrs(LVP_DEPT_OF_PATH[row.key]); return <td style={{ ...cell, color: '#1565c0' }}>{lh > 0 && goal > 0 ? (goal / lh).toFixed(1) : '—'}</td>; })()}
              </tr>); })}
            {(() => {
              const ibP = QIDS.reduce((s, q) => s + ['pid', 'pr', 'aros', 'pit'].reduce((a, k) => a + schedCartons(k, q), 0), 0);
              const obP = ibP + (highCageWIP ? 1000 : 0);
              const ibGoal = g('pid') + g('pr') + g('aros') + g('pit');
              const obGoal = ibGoal + (highCageWIP ? 1000 : 0);
              const totalGrid = ibGridHours + obGridHours;
              const adminHrs = adminHrsLvp; // LVP admin, for the LVP comparison column
              const totalWithAdmin = totalGrid + adminGridHours; // Admin is now a grid row (OB Support)
              const rows = [
                { label: 'Total IB Cartons', planned: ibP, goal: ibGoal, hrs: ibGridHours, lvpHrs: lvpIBHrs },
                { label: 'Total OB Cartons', planned: obP, goal: obGoal, hrs: obGridHours, lvpHrs: lvpOBHrs },
                { label: 'Total Cartons', planned: ibP + obP, goal: ibGoal + obGoal, hrs: totalGrid, lvpHrs: lvpTotalHrs },
              ];
              return (<>
                {rows.map(r => (
                  <tr key={r.label} style={{ borderTop: '2px solid #000' }}>
                    <td style={{ ...cell, textAlign: 'left', fontWeight: 700 }}>{r.label}</td>
                    <td style={cell} colSpan={4}></td>
                    <td style={{ ...cell, fontWeight: 700 }}>{r.planned ? r.planned.toLocaleString() : '—'}</td>
                    <td style={{ ...cell, fontWeight: 700 }}>{r.goal ? r.goal.toLocaleString() : '—'}</td>
                    <td style={{ ...cell, fontWeight: 700 }}>{r.hrs ? Math.round(r.hrs) : '—'}</td>
                    <td style={{ ...cell, fontWeight: 700 }}>{r.hrs > 0 ? (r.planned / r.hrs).toFixed(1) : '—'}</td>
                    <td style={{ ...cell, fontWeight: 700, color: '#1565c0' }}>{r.lvpHrs > 0 && r.goal > 0 ? (r.goal / r.lvpHrs).toFixed(1) : '—'}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '3px double #000', background: '#e8f5e9' }}>
                  <td style={{ ...cell, textAlign: 'left', fontWeight: 700 }}>Bottom Line</td>
                  <td style={cell} colSpan={4}></td>
                  <td style={{ ...cell, fontWeight: 700 }}>{ibP ? ibP.toLocaleString() : '—'}</td>
                  <td style={cell}></td>
                  <td style={{ ...cell, fontWeight: 700 }} title={`IB+OB ${Math.round(totalGrid)} + Admin ${Math.round(adminGridHours)}`}>{totalWithAdmin ? Math.round(totalWithAdmin) : '—'}</td>
                  <td style={{ ...cell, fontWeight: 700 }}>{totalWithAdmin > 0 ? ((ibP + obP) / totalWithAdmin).toFixed(1) : '—'}</td>
                  <td style={{ ...cell, fontWeight: 700, color: '#1565c0' }} title="LVP Diluted CPLH">{lvpTotalHrs > 0 ? ((ibGoal + obGoal) / (lvpTotalHrs + adminHrs)).toFixed(1) : '—'}</td>
                </tr>
              </>);
            })()}
          </tbody>
        </table>
        <div style={{ fontSize: '0.66rem', color: '#888', marginTop: 6 }}>Per-quarter cells = cartons produced = crew × Active CPH × (quarter BRT ÷ 60). Planned = sum across quarters (green if within 10% of the LVP Goal, amber if not). Total Hrs = all roles in the dept from the allocation grid; Rate = Planned ÷ Total Hrs. LVP Rate = LVP Goal ÷ LVP dept hours (the same comparison in LVP). Goals and rates come straight from the week's LVP plan (PR = total − PID − AROS; LVP plans SKID inside PR). With Absorb surplus HC on, spare heads under the cap become extra volume (up to 1.25× goal), so Planned and Rate run above LVP. Set a per-quarter cell to 0 to zero-staff that path for the quarter (HC redistributes to other paths).</div>
      </div>

      {/* ===== PHASE 2: allocation by dept ===== */}
      <div className="card">
        <h2 className="section-title">🧩 Ideal Allocation by Quarter</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.75rem', marginBottom: 8, flexWrap: 'wrap' }}>
          <strong>HC cap / quarter:</strong>
          {QIDS.map(q => <label key={q}>{q}<input type="number" value={capOverride[q] !== undefined ? capOverride[q] : ''} placeholder={String(phase1.find(p => p.id === q)?.hc || 0)} onChange={e => setCapOverride({ ...capOverride, [q]: e.target.value })} style={{ width: 55, marginLeft: 3, textAlign: 'center' }} /></label>)}
          <button className="btn btn-secondary" style={{ fontSize: '0.65rem', padding: '2px 8px' }} onClick={() => { const v = capOverride.Q1 !== undefined && capOverride.Q1 !== '' ? capOverride.Q1 : String(phase1.find(p => p.id === 'Q1')?.hc || 0); setCapOverride({ Q1: v, Q2: v, Q3: v, Q4: v }); }}>Q1 ⟹ All</button>
          <button className="btn btn-secondary" style={{ fontSize: '0.65rem', padding: '2px 8px' }} onClick={() => setCapOverride({})}>↺ Use Forecast</button>
          <button className="btn btn-primary" style={{ fontSize: '0.65rem', padding: '2px 10px', marginLeft: 'auto' }} onClick={savePlan}>{saved ? '✅ Saved!' : '💾 Save Plan'}</button>
        </div>
        <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 920 }}>
          <thead><tr><th style={{ ...hdr, textAlign: 'left' }}>Role</th>{QIDS.map(q => <th key={q} style={hdr}>{q}</th>)}<th style={hdr}>Total Hrs</th><th style={hdr}>Exp Rate</th><th style={hdr}>LVP Hrs</th><th style={hdr}>LVP Rate</th></tr></thead>
          <tbody>
            {DEPTS.map(dep => (
              <React.Fragment key={dep.key}>
                {!dep.standalone && <tr><td colSpan={9} style={{ ...cell, textAlign: 'left', fontWeight: 700, background: '#f0f0f0' }}>{dep.name}</td></tr>}
                {dep.functions.map(f => (
                  <tr key={dep.key + f.casual + f.fn}>
                    <td style={{ ...cell, textAlign: 'left', fontWeight: dep.standalone ? 700 : 400, background: dep.standalone ? '#f0f0f0' : undefined }}>{f.casual}<span style={{ color: '#999', fontSize: '0.6rem', fontWeight: 400 }}> ({f.fn})</span></td>
                    {QIDS.map(q => {
                      const k = cellKey(dep, f, q);
                      const absorbed = extraForCell(dep, f, q) + (bucketExtra[k] || 0); // spare HC placed here by Absorb surplus
                      const auto = Math.round((base[k] || 0) + absorbed); // rule-based HC + absorbed HC
                      const ov = cellOverride[k] !== undefined && cellOverride[k] !== '';
                      return <td key={q} title={!ov && absorbed > 0 ? `+${absorbed} absorbed surplus HC` : undefined} style={{ ...cell, padding: 1, background: ov ? '#fff3e0' : (absorbed > 0 ? '#e3f2fd' : (auto > 0 ? '#f5fff5' : 'transparent')) }}>
                        <input type="number" value={ov ? cellOverride[k] : (auto || '')} placeholder={auto ? String(auto) : '0'} onChange={e => setCellOverride({ ...cellOverride, [k]: e.target.value })} style={inp} />
                      </td>;
                    })}
                    {(() => {
                      const hrs = roleHours(dep, f); const dc = roleVol(f.casual, dep.key);
                      const lvpH = lvpHoursRaw(f.casual);   // LVP reference columns stay LVP's own numbers
                      const expR = hrs > 0 ? dc / hrs : 0; const lvpR = lvpH > 0 ? dc / lvpH : 0;
                      return (<>
                        <td style={{ ...cell, fontWeight: 600 }}>{hrs ? Math.round(hrs) : '—'}</td>
                        <td style={cell}>{expR ? expR.toFixed(1) : '—'}</td>
                        <td style={cell}>{lvpH ? Math.round(lvpH) : '—'}</td>
                        <td style={cell}>{lvpR ? lvpR.toFixed(1) : '—'}</td>
                      </>);
                    })()}
                  </tr>
                ))}
                {!dep.standalone && <tr><td style={{ ...cell, textAlign: 'left', fontWeight: 600, color: '#1565c0' }}>{dep.name} Total</td>{QIDS.map(q => <td key={q} style={{ ...cell, fontWeight: 600, color: '#1565c0' }}>{dep.functions.reduce((a, f) => a + val(dep, f, q), 0)}</td>)}<td style={{ ...cell, fontWeight: 600, color: '#1565c0' }}>{Math.round(dep.functions.reduce((a, f) => a + roleHours(dep, f), 0)) || '—'}</td><td style={cell}></td><td style={{ ...cell, fontWeight: 600, color: '#1565c0' }}>{Math.round(dep.functions.reduce((a, f) => a + lvpHoursFor(f.casual), 0)) || '—'}</td><td style={cell}></td></tr>}
              </React.Fragment>
            ))}
            <tr style={{ borderTop: '2px solid #000', fontWeight: 700 }}><td style={{ ...cell, textAlign: 'left' }}>TOTAL HC</td>{QIDS.map(q => { const t = totalHC[q]; const cap = capFor(q); return <td key={q} style={{ ...cell, fontWeight: 700, background: t > cap * 1.001 ? '#ffcdd2' : '#c8e6c9' }}>{t} / {cap}</td>; })}<td style={{ ...cell, fontWeight: 700 }}>{Math.round(DEPTS.reduce((s, dep) => s + dep.functions.reduce((a, f) => a + roleHours(dep, f), 0), 0)) || '—'}</td><td style={cell}></td><td style={{ ...cell, fontWeight: 700 }}>{Math.round(DEPTS.reduce((s, dep) => s + dep.functions.reduce((a, f) => a + lvpHoursFor(f.casual), 0), 0)) || '—'}</td><td style={cell}></td></tr>
          </tbody>
        </table>
        <div style={{ fontSize: '0.66rem', color: '#888', marginTop: 8 }}>Roles grouped by department (mirrors HWM Comparison), incl. IB/OB Support; Admin is a standalone row. Primary unloaders auto-scheduled by goal ÷ rate level-loaded to caps; support/ratio/conditional roles derived from the ops role model (IB PSolver never below its LVP hours). With Absorb surplus HC on, spare HC under each quarter's cap goes (1) onto running paths up to 1.25× goal, (2) over buckets: rule-capped support roles trued up toward their LVP HC (up to each role's max), then (3) flow faster: any remainder onto running paths. Blue = absorbed HC (hover for count), orange = override, green = auto.</div>
      </div>
      </fieldset>
    </div>
  );
}
